import { LitElement, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import type { DevToolsPlugin } from '../../types';
import { styles } from './feature-flags.styles';

const FLAG_KEYS: { key: string; readonly?: boolean }[] = [
  { key: 'enable3DS', readonly: true },
  { key: 'enableAppsIntegration' },
  { key: 'enableCORSCheckout' },
  { key: 'enableCustomerMerge' },
  { key: 'enableEnhancedTemplatePublish' },
  { key: 'enableMembershipEmailValidation' },
  { key: 'enableSalesTax' },
  { key: 'enableSalesView' },
];

@customElement('acme-devtools-feature-flags')
export class AcmeDevToolsFeatureFlags extends LitElement {
  static override styles = styles;

  private get _devTools() {
    return (window as any).__ACME_B2B__?.devTools;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.requestUpdate();
  }

  private _resetAll() {
    const devTools = this._devTools;
    if (!devTools) {
      return;
    }

    if (devTools.setFeatureFlagOverride) {
      FLAG_KEYS.forEach(({ key }) => devTools.setFeatureFlagOverride!(key, null));
    }

    this.requestUpdate();
  }

  private _toggle(key: string, effective: boolean, isOverridden: boolean) {
    const devTools = this._devTools;
    if (!devTools?.setFeatureFlagOverride) {
      return;
    }
    devTools.setFeatureFlagOverride(key, isOverridden ? null : !effective);
    this.requestUpdate();
  }

  override render() {
    const devTools = this._devTools;

    if (!devTools) {
      return html`<div class="empty">Feature services not available yet.</div>`;
    }

    const overrides: Record<string, boolean> = devTools.getFeatureFlagOverrides?.() ?? {};
    const hasOverrides = Object.keys(overrides).length > 0;

    return html`
      <div class="toolbar">
        <button
          class="btn ${hasOverrides ? 'btn-danger' : 'btn-disabled'}"
          ?disabled=${!hasOverrides}
          @click=${() => this._resetAll()}>
          Reset All
        </button>
      </div>

      <h3 class="section-heading">Feature Flags (Server)</h3>

      ${FLAG_KEYS.map(({ key, readonly: isReadonly }) => {
        const effective: boolean = devTools.getFeatureFlag?.(key) ?? false;
        const original: boolean = devTools.getOriginalFeatureFlag?.(key) ?? effective;
        const isOverridden =
          devTools.getFeatureFlagOverrides != null && key in (devTools.getFeatureFlagOverrides() ?? {});
        const color = effective ? '#98c379' : '#5c6370';

        return html`
          <div class="flag-row ${isOverridden ? 'flag-row--overridden' : ''}">
            <div class="label-group">
              <code class="flag-key">${key}</code>
              ${isOverridden ? html`<span class="override-badge">was ${original ? 'ON' : 'OFF'}</span>` : nothing}
            </div>
            <span class="status">
              <span class="status-dot" style="color:${color}">${effective ? '●' : '○'}</span>
              <span class="status-label" style="color:${color}">${effective ? 'ON' : 'OFF'}</span>
            </span>
            ${devTools.setFeatureFlagOverride && !isReadonly
              ? html`<button
                  class="toggle-btn ${isOverridden ? 'toggle-btn--clear' : 'toggle-btn--override'}"
                  @click=${() => this._toggle(key, effective, isOverridden)}>
                  ${isOverridden ? 'Clear' : 'Override'}
                </button>`
              : html`<div></div>`}
          </div>
        `;
      })}
      ${hasOverrides ? html`<div class="hint">Navigate or reload to apply overrides.</div>` : nothing}
      ${this.renderLunchLitelySection()}
    `;
  }

  private renderLunchLitelySection() {
    if (!window.CONFIG.lunchLitely.apiKey) {
      return nothing;
    }

    const url = `${window.CONFIG.lunchLitely.baseUrl}/admin/flags?acme=${window.CONFIG.ENVIRONMENT_NAME}`;

    return html`
      <h3 class="section-heading">Feature Flags (Lunch Litely)</h3>
      <div class="empty">
        ${window.CONFIG?.lunchLitely?.baseUrl
          ? html`<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
          : nothing}
      </div>
    `;
  }
}

export const featureFlagsPlugin: DevToolsPlugin = {
  id: 'feature-flags',
  label: 'Feature Flags',
  tagName: 'acme-devtools-feature-flags',
};

declare global {
  interface HTMLElementTagNameMap {
    'acme-devtools-feature-flags': AcmeDevToolsFeatureFlags;
  }
}
